/**
 * OAuth Callback Handler Component
 * Handles the OAuth callback redirect and processes the authorization code
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface OAuthCallbackProps {
  onSuccess?: (tokenData: any) => void;
  onError?: (error: string) => void;
  redirectTo?: string;
}

export const OAuthCallback: React.FC<OAuthCallbackProps> = ({
  onSuccess,
  onError,
  redirectTo = '/dashboard'
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing OAuth callback...');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Extract callback parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth errors
        if (error) {
          const errorMsg = errorDescription || error || 'OAuth authentication failed';
          setStatus('error');
          setMessage(errorMsg);
          onError?.(errorMsg);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          const errorMsg = 'Missing authorization code or state parameter';
          setStatus('error');
          setMessage(errorMsg);
          onError?.(errorMsg);
          return;
        }

        // Retrieve stored PKCE parameters
        const storedState = sessionStorage.getItem('oauth_state');
        const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
        const redirectUri = sessionStorage.getItem('oauth_redirect_uri');

        // Validate state parameter (CSRF protection)
        if (state !== storedState) {
          const errorMsg = 'Invalid state parameter - possible CSRF attack';
          setStatus('error');
          setMessage(errorMsg);
          onError?.(errorMsg);
          return;
        }

        if (!codeVerifier || !redirectUri) {
          const errorMsg = 'Missing PKCE parameters - please restart OAuth flow';
          setStatus('error');
          setMessage(errorMsg);
          onError?.(errorMsg);
          return;
        }

        setMessage('Exchanging authorization code for tokens...');

        // Exchange authorization code for tokens
        const response = await fetch('/api/auth/oauth/microsoft/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            code_verifier: codeVerifier,
            redirect_uri: redirectUri,
            state
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Token exchange failed');
        }

        const tokenData = await response.json();

        // Clean up session storage
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_code_verifier');
        sessionStorage.removeItem('oauth_redirect_uri');

        // TASK-548(c): store under the OAuth-specific key, NOT auth_token — this token has no
        // account_id and would 401 every app request if it became the session token.
        if (tokenData.access_token) {
          localStorage.setItem('ms_oauth_token', tokenData.access_token);
        }

        setStatus('success');
        setMessage('Authentication successful! Redirecting...');
        onSuccess?.(tokenData);

        // Redirect after success
        setTimeout(() => {
          navigate(redirectTo);
        }, 2000);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'OAuth callback processing failed';
        setStatus('error');
        setMessage(errorMessage);
        onError?.(errorMessage);

        // Clean up session storage on error
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_code_verifier');
        sessionStorage.removeItem('oauth_redirect_uri');
      }
    };

    processCallback();
  }, [searchParams, navigate, onSuccess, onError, redirectTo]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Loader2 className="h-8 w-8 animate-spin text-blue-600" />;
      case 'success':
        return <CheckCircle className="h-8 w-8 text-green-600" />;
      case 'error':
        return <XCircle className="h-8 w-8 text-red-600" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'border-blue-200 bg-blue-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className={`w-full max-w-md ${getStatusColor()}`}>
        <CardContent className="flex flex-col items-center text-center p-8 space-y-4">
          {getStatusIcon()}
          
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              {status === 'processing' && 'Processing Authentication'}
              {status === 'success' && 'Authentication Successful'}
              {status === 'error' && 'Authentication Failed'}
            </h2>
            
            <p className="text-gray-600">{message}</p>
          </div>

          {status === 'error' && (
            <div className="w-full">
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  Please close this window and try again. If the problem persists, 
                  contact support.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {status === 'success' && (
            <div className="text-sm text-gray-500">
              You will be redirected automatically...
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthCallback;